import React from "react";
import { EventBus } from "@engine/EventBus.js";
import { ShopStore } from "@store/ShopStore.js";
import { currencyAbbrev } from "../../engine/adventure/ActiveAdventure.js";
import { PartyStore } from "@store/PartyStore.js";
import { buyItem, COPPER_PER_GOLD, type Shop } from "@engine/inventory/shop.js";
import { pushFeed } from "@engine/Feed.js";

/**
 * I07b — shop modal.
 *
 * Subscribes to `narrative:open_shop` and mounts a buy pane for the
 * matching `ShopStore` entry. On purchase: calls pure `buyItem`, writes
 * the new shop snapshot back via `ShopStore.update`, spends the party
 * gold, and emits `inventory:gold_changed`. Closing the modal unpauses
 * the Ink flow — the narrative machine re-enters on the next step.
 *
 * Sell pane is deferred until character-inventory UI lands; the pure
 * `sellItem` helper already exists for that path.
 */
export function ShopScreen() {
  const [shopkeeperId, setShopkeeperId] = React.useState<string | null>(null);
  const [gold, setGold] = React.useState(PartyStore.getGold());
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    const offOpen = EventBus.on("narrative:open_shop", ({ npcId }) => {
      if (!ShopStore.get(npcId)) {
        pushFeed(`No shop registered for ${npcId}.`);
        return;
      }
      setShopkeeperId(npcId);
    });
    const offGold = EventBus.on("inventory:gold_changed", ({ gold: g }) => {
      setGold(g);
    });
    return () => {
      offOpen();
      offGold();
    };
  }, []);

  if (!shopkeeperId) return null;
  const shop = ShopStore.get(shopkeeperId);
  if (!shop) return null;

  const close = () => setShopkeeperId(null);

  const onBuy = (itemId: string) => {
    const partyGoldCp = PartyStore.getGold() * COPPER_PER_GOLD;
    const result = buyItem(shop, itemId, partyGoldCp);
    if (!result.ok) {
      pushFeed(
        result.failure === "insufficient_gold"
          ? "You can't afford that."
          : result.failure === "out_of_stock"
            ? "Out of stock."
            : "That item isn't for sale.",
      );
      return;
    }
    ShopStore.update(result.shop);
    const spentGold = result.costCp / COPPER_PER_GOLD;
    PartyStore.spendGold(spentGold);
    EventBus.emit("inventory:gold_changed", {
      gold: PartyStore.getGold(),
      delta: -spentGold,
    });
    EventBus.emit("inventory:give_item", { itemId, qty: 1 });
    setTick((t) => t + 1);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        zIndex: 200,
      }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520,
          maxWidth: "92vw",
          maxHeight: "80vh",
          overflowY: "auto",
          background: "#101420",
          color: "#e8e8e8",
          border: "1px solid #445",
          borderRadius: 4,
          boxShadow: "0 4px 24px rgba(0,0,0,0.7)",
          padding: "18px 22px",
          fontFamily: "monospace",
        }}
        data-tick={tick}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            borderBottom: "1px solid #334",
            paddingBottom: 10,
            marginBottom: 14,
          }}
        >
          <div style={{ fontSize: 14, letterSpacing: 1, textTransform: "uppercase", color: "#aab" }}>
            {shop.shopkeeperId}
          </div>
          <div style={{ fontSize: 12, color: "#f5a623" }}>{gold} {currencyAbbrev()}</div>
        </div>

        {shop.stock.length === 0 ? (
          <p style={{ color: "#889", fontSize: 13 }}>Nothing for sale right now.</p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            {shop.stock.map((entry) => {
              const outOfStock = entry.quantity <= 0;
              const canAfford = gold >= entry.priceGold;
              const disabled = outOfStock || !canAfford;
              return (
                <li
                  key={entry.itemId}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#1a2030",
                    border: "1px solid #334",
                    borderRadius: 2,
                    padding: "8px 12px",
                    fontSize: 13,
                  }}
                >
                  <span>
                    <span style={{ color: "#cce" }}>{entry.itemId}</span>
                    <span style={{ color: "#778", marginLeft: 8 }}>
                      {Number.isFinite(entry.quantity) ? `×${entry.quantity}` : "×∞"}
                    </span>
                  </span>
                  <button
                    onClick={() => onBuy(entry.itemId)}
                    disabled={disabled}
                    style={{
                      background: disabled ? "#222" : "#2a3a5a",
                      color: disabled ? "#556" : "#cce",
                      border: "1px solid #446",
                      borderRadius: 2,
                      padding: "4px 12px",
                      cursor: disabled ? "not-allowed" : "pointer",
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                  >
                    Buy · {entry.priceGold} {currencyAbbrev()}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button
            onClick={close}
            style={{
              background: "#334",
              color: "#cce",
              border: "1px solid #557",
              padding: "6px 18px",
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: 12,
              borderRadius: 2,
            }}
          >
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}
